import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Activity, CheckCheck, ChevronDown, Loader2, X } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useClientActivity, useMarkActivitySeen } from '../../../services/clientOnboarding';
import { CLIENT_ACTIVITY_TAB, activityLine, ago } from './clientActivityText';

// Client activity (Nic 2026-10-23): one button in the Workspace header, not a
// stack of toasts. An icon and a count of what your clients did that you
// haven't seen; a soft pulse when the count goes up. It opens a drawer from
// the right (Nic: a pop-up was too much in too little room): New in its own
// box, then Seen, the latest few with Show more. Opening a line opens that
// client where the change shows. "Seen" lives on the server per person, so a
// cleared line never comes back (another computer, a refresh, a cleared
// cache).

const PULSE_MS = 2400;
const SEEN_FIRST = 5;
const SEEN_MORE = 10;
const SLIDE_MS = 220;

const SectionHeading = ({ label, count, tone = 'slate' }) => (
  <div className="mb-2 flex items-center gap-2 px-1">
    <span className="text-[12px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</span>
    {count ? (
      <span
        className={cn(
          'min-w-[1.25rem] rounded-full px-1.5 text-center text-[11px] font-semibold tabular-nums leading-[18px]',
          tone === 'sky'
            ? 'bg-sky-600 text-white dark:bg-sky-500'
            : 'bg-slate-900/[0.06] text-slate-500 dark:!bg-white/10 dark:text-slate-400',
        )}
      >
        {count}
      </span>
    ) : null}
  </div>
);

const ClientActivityButton = ({ viewOnly = false, labels = {}, onOpenClient, now = new Date() }) => {
  // open: the drawer is wanted; mounted/shown drive the slide in and out.
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);
  const [scope, setScope] = useState('mine');
  const [seenShown, setSeenShown] = useState(SEEN_FIRST);
  const activity = useClientActivity(scope);
  const markSeen = useMarkActivitySeen();
  const closeRef = useRef(null);

  const data = activity.data;
  const items = data?.items || [];
  const newCount = data?.newCount || 0;
  // Managers switch between their own and everyone's; view only (who owns
  // nobody) always sees everyone, so no switch.
  const canSwitch = !!data?.canSeeAll && !viewOnly;

  // The pulse: only when the count rises after the first load (never on
  // arrival, never on a scope switch).
  const [pulse, setPulse] = useState(0);
  const lastCount = useRef(null);
  const lastScope = useRef(scope);
  useEffect(() => {
    if (!data) return undefined;
    const prev = lastCount.current;
    const sameScope = lastScope.current === scope;
    lastCount.current = newCount;
    lastScope.current = scope;
    if (prev == null || !sameScope || newCount <= prev) return undefined;
    setPulse((n) => n + 1);
    return undefined;
  }, [data, newCount, scope]);
  const [pulsing, setPulsing] = useState(false);
  useEffect(() => {
    if (!pulse) return undefined;
    setPulsing(true);
    const t = setTimeout(() => setPulsing(false), PULSE_MS);
    return () => clearTimeout(t);
  }, [pulse]);

  // Slide in on open; slide out, then unmount, on close.
  useEffect(() => {
    if (open) {
      setMounted(true);
      setSeenShown(SEEN_FIRST);
      const raf = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(raf);
    }
    setShown(false);
    const t = setTimeout(() => setMounted(false), SLIDE_MS);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, mounted]);

  const openItem = (item) => {
    setOpen(false);
    onOpenClient?.(item.onboardingId, CLIENT_ACTIVITY_TAB[item.type] || null);
  };

  const countLabel = newCount > 99 ? '99+' : String(newCount);
  const fresh = items.filter((i) => i.isNew);
  const seen = items.filter((i) => !i.isNew);
  const seenVisible = seen.slice(0, seenShown);
  const seenLeft = seen.length - seenVisible.length;

  const row = (item) => (
    <li key={item.eventId}>
      <button
        type="button"
        onClick={() => openItem(item)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left outline-none hover:bg-slate-50 focus:outline-none focus-visible:bg-sky-50 dark:hover:!bg-white/[0.04] dark:focus-visible:!bg-sky-500/10"
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span
              className={cn(
                'truncate text-[13px] font-semibold',
                item.isNew ? 'text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-300',
              )}
            >
              {item.name}
            </span>
            <span className="ml-auto shrink-0 text-[11px] tabular-nums text-slate-400 dark:text-slate-500">
              {ago(item.at, now)}
            </span>
          </span>
          <span
            className={cn(
              'mt-0.5 line-clamp-2 block text-[13px] leading-snug',
              item.isNew ? 'text-slate-600 dark:text-slate-300' : 'text-slate-500 dark:text-slate-400',
            )}
          >
            {activityLine(item.type, item.meta, labels)}
          </span>
        </span>
        {item.isNew ? (
          <span aria-label="New" className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-sky-500" />
        ) : null}
      </button>
    </li>
  );

  const drawer = mounted
    ? createPortal(
        <div className="fixed inset-0 z-[1050]" role="presentation">
          {/* The page stays in view behind a light veil; a click on it closes. */}
          <div
            aria-hidden
            onClick={() => setOpen(false)}
            className={cn(
              'absolute inset-0 bg-slate-900/20 transition-opacity motion-reduce:transition-none dark:bg-black/40',
              shown ? 'opacity-100' : 'opacity-0',
            )}
            style={{ transitionDuration: `${SLIDE_MS}ms` }}
          />
          <aside
            role="dialog"
            aria-modal="true"
            aria-label="Client activity"
            className={cn(
              'absolute inset-y-0 right-0 flex w-full flex-col bg-slate-50 shadow-[-12px_0_40px_rgba(15,23,42,0.18)] transition-transform ease-out motion-reduce:transition-none dark:!bg-surface-page sm:w-[420px]',
              shown ? 'translate-x-0' : 'translate-x-full',
            )}
            style={{ transitionDuration: `${SLIDE_MS}ms` }}
          >
            <header className="border-b border-slate-200/80 bg-white px-5 pb-3 pt-4 dark:border-white/10 dark:!bg-surface-card">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="mb-0 text-[17px] font-bold leading-tight text-slate-900 dark:text-white">Client activity</h3>
                  <div className="mt-0.5 text-[12px] text-slate-500 dark:text-slate-400">
                    What {scope === 'all' || viewOnly ? 'clients' : 'your clients'} did in the last 14 days
                  </div>
                </div>
                <button
                  ref={closeRef}
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close client activity"
                  className="-mr-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 outline-none hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:hover:!bg-white/10 dark:hover:text-white"
                >
                  <X size={17} />
                </button>
              </div>
              <div className="mt-3 flex items-center gap-2">
                {canSwitch ? (
                  <div className="flex rounded-full bg-slate-100 p-0.5 dark:!bg-white/10">
                    {[
                      ['mine', 'Mine'],
                      ['all', 'Everyone'],
                    ].map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        aria-pressed={scope === key}
                        onClick={() => {
                          setScope(key);
                          setSeenShown(SEEN_FIRST);
                        }}
                        className={cn(
                          'rounded-full px-3 py-1 text-[12px] font-semibold outline-none focus:outline-none',
                          scope === key
                            ? 'bg-white text-slate-800 shadow-sm dark:!bg-surface-elevated dark:text-white'
                            : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={() => markSeen.mutate({})}
                  disabled={!newCount}
                  className="ml-auto inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-[12px] font-semibold text-sky-700 outline-none hover:bg-sky-50 focus:outline-none disabled:pointer-events-none disabled:opacity-40 dark:text-sky-300 dark:hover:!bg-sky-500/15"
                >
                  <CheckCheck size={14} /> Mark all seen
                </button>
              </div>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 pt-4">
              {activity.isLoading ? (
                <div className="flex h-32 items-center justify-center">
                  <Loader2 size={18} className="animate-spin text-slate-400" />
                </div>
              ) : activity.isError ? (
                <div className="rounded-xl bg-rose-50 px-4 py-3 text-[13px] text-rose-700 dark:!bg-rose-500/10 dark:text-rose-300">
                  Client activity couldn't be loaded.
                </div>
              ) : (
                <>
                  <section>
                    <SectionHeading label="New" count={fresh.length} tone="sky" />
                    {fresh.length ? (
                      <ul className="mb-0 list-none divide-y divide-slate-100 overflow-hidden rounded-xl bg-white pl-0 shadow-[0_1px_2px_rgba(15,23,42,0.04)] ring-1 ring-sky-200/70 dark:divide-white/[0.06] dark:!bg-surface-card dark:ring-sky-500/25">
                        {fresh.map(row)}
                      </ul>
                    ) : (
                      <div className="px-1 text-[13px] italic text-slate-400 dark:text-slate-500">You're all caught up</div>
                    )}
                  </section>

                  <section className="mt-6">
                    <SectionHeading label="Seen" count={seen.length} />
                    {seen.length ? (
                      <>
                        <ul className="mb-0 list-none divide-y divide-slate-100 overflow-hidden rounded-xl bg-white pl-0 ring-1 ring-slate-200/70 dark:divide-white/[0.06] dark:!bg-surface-card dark:ring-white/10">
                          {seenVisible.map(row)}
                        </ul>
                        {seenLeft > 0 ? (
                          <button
                            type="button"
                            onClick={() => setSeenShown((n) => n + SEEN_MORE)}
                            className="mx-auto mt-3 flex items-center gap-1 rounded-full px-3 py-1.5 text-[12px] font-semibold text-slate-500 outline-none hover:bg-white hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:text-slate-400 dark:hover:!bg-white/5 dark:hover:text-white"
                          >
                            Show more <span className="tabular-nums text-slate-400">({seenLeft})</span>
                            <ChevronDown size={14} />
                          </button>
                        ) : null}
                      </>
                    ) : (
                      <div className="px-1 text-[13px] italic text-slate-400 dark:text-slate-500">
                        {items.length
                          ? 'Anything you open or mark seen moves here.'
                          : `Nothing from ${scope === 'all' || viewOnly ? 'any clients' : 'your clients'} in the last 14 days.`}
                      </div>
                    )}
                  </section>
                </>
              )}
            </div>
          </aside>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title={newCount ? `${newCount} new from your clients` : 'What your clients did'}
        aria-label={newCount ? `Client activity, ${newCount} new` : 'Client activity'}
        className={cn(
          'relative inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full bg-white px-2.5 text-[12px] font-semibold shadow-[0_1px_2px_rgba(15,23,42,0.06)] outline-none ring-1 ring-slate-200/80 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:!bg-white/5 dark:ring-white/10 dark:hover:!bg-white/10',
          newCount ? 'text-sky-700 dark:text-sky-300' : 'text-slate-500 hover:text-slate-800 dark:text-slate-300 dark:hover:text-white',
        )}
      >
        {pulsing ? (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 animate-ping-soft rounded-full shadow-[0_0_0_1.5px_rgba(14,165,233,0.8)] motion-reduce:hidden"
          />
        ) : null}
        <Activity size={15} strokeWidth={2.25} />
        {newCount ? (
          <span className="min-w-[1.25rem] rounded-full bg-sky-600 px-1.5 text-center text-[11px] tabular-nums leading-[18px] text-white dark:bg-sky-500">
            {countLabel}
          </span>
        ) : null}
      </button>
      {drawer}
    </>
  );
};

export default ClientActivityButton;
